import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Edit, Loader2, Trash2 } from 'lucide-react';
import { payrollApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { cn, formatCurrency } from '../../lib/utils';

type BenefitStatus = 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';

interface BenefitFormData {
  name: string;
  benefit_type: string;
  provider: string;
  employee_cost: number;
  employer_cost: number;
  is_active: boolean;
  status: BenefitStatus;
}

const Benefits: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState<any>(null);
  const [formData, setFormData] = useState<BenefitFormData>({
    name: '',
    benefit_type: '',
    provider: '',
    employee_cost: 0,
    employer_cost: 0,
    is_active: true,
    status: 'ACTIVE',
  });

  const { data: benefits = [], isLoading, error } = useQuery({
    queryKey: ['benefits'],
    queryFn: () => payrollApi.benefits.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: payrollApi.benefits.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: 'Benefit created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => payrollApi.benefits.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      setIsDialogOpen(false);
      setEditingBenefit(null);
      resetForm();
      toast({ title: 'Benefit updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => payrollApi.benefits.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      toast({ title: 'Benefit deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const statusChangeMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: BenefitStatus }) => 
      payrollApi.benefits.update(id, { status, is_active: status === 'ACTIVE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      toast({ title: 'Benefit status updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleDelete = (benefit: any) => {
    if (window.confirm(`Are you sure you want to delete the benefit "${benefit.name}"?`)) {
      deleteMutation.mutate(benefit.id);
    }
  };

  const handleStatusChange = (benefitId: string, newStatus: BenefitStatus) => {
    statusChangeMutation.mutate({ id: benefitId, status: newStatus });
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.benefit_type) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    if (editingBenefit) {
      updateMutation.mutate({ id: editingBenefit.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const openDialog = (benefit?: any) => {
    if (benefit) {
      setEditingBenefit(benefit);
      setFormData({
        name: benefit.name,
        benefit_type: benefit.benefit_type,
        provider: benefit.provider || '',
        employee_cost: benefit.employee_cost,
        employer_cost: benefit.employer_cost,
        is_active: benefit.is_active,
        status: benefit.status || 'ACTIVE',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      benefit_type: '',
      provider: '',
      employee_cost: 0,
      employer_cost: 0,
      is_active: true,
      status: 'ACTIVE',
    });
    setEditingBenefit(null);
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400',
      INACTIVE: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400',
      DISCONTINUED: 'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400',
    };
    const classNames = colors[status?.toUpperCase()] || 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0 capitalize", classNames)} variant="outline">
        {status?.toLowerCase()}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 mt-3 font-bold text-xs text-slate-400">Loading benefits...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl text-xs font-semibold m-6">
        <p className="font-bold text-sm">Error loading benefits</p>
        <p className="mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Manage employee benefits and plans</h2>
        </div>
        <Button 
          onClick={() => openDialog()} 
        >
          <Plus className="h-4 w-4 mr-1.5 shrink-0" />
          <span>Add Benefit</span>
        </Button>
      </div>

      {/* Main Table Card */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Benefits</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-55 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Benefit Name</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Type</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Provider</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Employee Cost</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Employer Cost</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {benefits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs animate-pulse">
                    No benefits found. Create your first benefit plan.
                  </TableCell>
                </TableRow>
              ) : (
                benefits.map((benefit: any) => (
                  <TableRow key={benefit.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50">
                    <TableCell className="px-8 py-4">
                      <div className="flex items-center space-x-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[#143A62]/10 dark:bg-blue-600/20 text-[#143A62] dark:text-blue-400 font-bold text-[11px] flex items-center justify-center tracking-wider shrink-0 uppercase">
                          {benefit.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                        </div>
                        <span className="font-bold text-xs text-[#143A62] dark:text-blue-400">{benefit.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-semibold text-slate-700 dark:text-slate-300 py-4 capitalize">
                      {benefit.benefit_type?.toLowerCase()}
                    </TableCell>
                    <TableCell className="text-xs font-semibold text-slate-500 py-4">{benefit.provider || 'N/A'}</TableCell>
                    <TableCell className="text-xs font-bold text-slate-700 py-4">{formatCurrency(benefit.employee_cost)}</TableCell>
                    <TableCell className="text-xs font-bold text-slate-700 py-4">{formatCurrency(benefit.employer_cost)}</TableCell>
                    <TableCell className="py-4">
                      <Select
                        value={benefit.status || 'ACTIVE'}
                        onValueChange={(value) => handleStatusChange(benefit.id, value as BenefitStatus)}
                      >
                        <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 h-9 w-[130px] rounded-xl shadow-sm px-2.5">
                          {getStatusBadge(benefit.status || 'ACTIVE')}
                        </SelectTrigger>
                        <SelectContent className="border-slate-100 dark:border-slate-800">
                          <SelectItem value="ACTIVE" className="text-xs font-bold">Active</SelectItem>
                          <SelectItem value="INACTIVE" className="text-xs font-bold">Inactive</SelectItem>
                          <SelectItem value="DISCONTINUED" className="text-xs font-bold">Discontinued</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="px-8 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openDialog(benefit)}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(benefit)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">
              {editingBenefit ? 'Edit Benefit' : 'Create Benefit'}
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Configure benefit details
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-bold text-slate-400 tracking-wider">Benefit Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Health Insurance"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="benefit_type" className="text-xs font-bold text-slate-400 tracking-wider">Type *</Label>
              <Input
                id="benefit_type"
                value={formData.benefit_type}
                onChange={(e) => setFormData({ ...formData, benefit_type: e.target.value })}
                placeholder="e.g., Medical, Dental, 401k"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="provider" className="text-xs font-bold text-slate-400 tracking-wider">Provider</Label>
              <Input
                id="provider"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                placeholder="Benefit provider name"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="employee_cost" className="text-xs font-bold text-slate-400 tracking-wider">Employee Cost</Label>
                <Input
                  id="employee_cost"
                  type="number"
                  value={formData.employee_cost}
                  onChange={(e) => setFormData({ ...formData, employee_cost: parseFloat(e.target.value) || 0 })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="employer_cost" className="text-xs font-bold text-slate-400 tracking-wider">Employer Cost</Label>
                <Input
                  id="employer_cost"
                  type="number"
                  value={formData.employer_cost}
                  onChange={(e) => setFormData({ ...formData, employer_cost: parseFloat(e.target.value) || 0 })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status" className="text-xs font-bold text-slate-400 tracking-wider">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value as BenefitStatus, is_active: value === 'ACTIVE' })}
              >
                <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-10 rounded-xl shadow-sm">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="border-slate-105 dark:border-slate-800">
                  <SelectItem value="ACTIVE" className="text-xs font-bold">Active</SelectItem>
                  <SelectItem value="INACTIVE" className="text-xs font-bold">Inactive</SelectItem>
                  <SelectItem value="DISCONTINUED" className="text-xs font-bold">Discontinued</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingBenefit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Benefits;